import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { initialState } from "./initialState";
import { createSelector } from "./selector";
import { Gantt, Identifiable } from "@planzone/dto";
import { reduceList } from "../../common/list";
import { reduceProject } from "../../common/project";

function handleRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleSuccess(
  state: State,
  constraints: Boundaries["retrieveConstraints"]["success"],
): State {
  return { constraints, request: setRequestStateToSuccess() };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleEditConstraint(
  state: State,
  { constraint }: Boundaries["editConstraint"]["success"],
): State {
  return {
    ...state,
    constraints: state.constraints.map((c) =>
      c.uuid === constraint.uuid ? { ...c, ...constraint } : c,
    ),
  };
}

function handleRemoveConstraint(
  state: State,
  constraint: Boundaries["removeConstraint"]["success"],
): State {
  return {
    ...state,
    constraints: state.constraints.filter((c) => c.uuid !== constraint.uuid),
  };
}

function propagateEvent(state: State = initialState, event: Event): State {
  const removedElement: Identifiable<any> =
    event.type === events.removeTaskSucceeded
      ? event.data
      : event.type === events.removeListSucceeded
        ? event.data
        : event.type === events.removeMilestoneSucceeded
          ? event.data
          : event.type === events.removeEventSucceeded
            ? event.data
            : null;
  const editedElement: Identifiable<any> =
    event.type === events.editTaskSucceeded
      ? event.data
      : event.type === events.editListSucceeded
        ? event.data
        : event.type === events.editMilestoneSucceeded
          ? event.data
          : event.type === events.editEventSucceeded
            ? event.data
            : null;

  return {
    ...state,
    constraints: removedElement
      ? state.constraints.filter(
          ({ originEntityUuid, targetEntityUuid }) =>
            originEntityUuid !== removedElement.uuid &&
            targetEntityUuid !== removedElement.uuid,
        )
      : editedElement
        ? state.constraints.map((constraint) =>
            constraint.originEntityUuid === editedElement.uuid
              ? {
                  ...constraint,
                  originStartDate: editedElement.startDate,
                  originEndDate: editedElement.endDate,
                }
              : constraint.targetEntityUuid === editedElement.uuid
                ? {
                    ...constraint,
                    targetStartDate: editedElement.startDate,
                    targetEndDate: editedElement.endDate,
                  }
                : constraint,
          )
        : state.constraints,
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveConstraintsRequested]: handleRequest,
      [events.retrieveConstraintsSucceeded]: handleSuccess,
      [events.retrieveConstraintsFailed]: handleFailure,
      [events.editConstraintSucceeded]: handleEditConstraint,
      [events.removeConstraintSucceeded]: handleRemoveConstraint,
    },
    propagateEvent,
  ),
  [
    events.changePlanzoneSucceeded,
    events.constrainElementsSucceeded,
    events.duplicateListSucceeded,
    events.duplicateProjectSucceeded,
    events.useProjectModelSucceeded,
    events.createProjectModelFromProjectSucceeded,
    events.markTaskAsCompletedSucceeded,
    events.markMilestoneAsCompletedSucceeded,
    events.editTaskSucceeded,
  ],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
